import type { BackgroundJob } from './BackgroundJobsContext';
import type { ShipmentBatchDetails } from '../lib/api';

function hasShipmentBatchFinished(batch: ShipmentBatchDetails): boolean {
  if (batch.status === 'COMPLETED' || batch.status === 'COMPLETED_WITH_ERRORS') return true;
  if (!batch.creationFinishedAt) return false;
  const settled = batch.counts.shipmentFailures
    + batch.counts.postProcessingCompleted
    + batch.counts.postProcessingFailed;
  return settled === batch.counts.total
    && batch.counts.waitingForShipment === 0
    && batch.counts.postProcessingPending === 0;
}

// Polling, finish responses and restored jobs must derive the lock from the same
// server state. A late response cannot reopen a batch already known to be done.
export function mergeBackgroundJobUpdate(
  job: BackgroundJob,
  update: Partial<Omit<BackgroundJob, 'id'>>,
): BackgroundJob {
  const next = { ...job, ...update };
  if (next.type !== 'bulk_shipment' || !next.serverBatchId || !next.shipmentBatch) return next;
  if (next.shipmentBatch.id !== next.serverBatchId) return job;

  const previousBatch = job.shipmentBatch;
  const batch = previousBatch?.id === next.serverBatchId
    && hasShipmentBatchFinished(previousBatch)
    && !hasShipmentBatchFinished(next.shipmentBatch)
    ? previousBatch
    : next.shipmentBatch;
  const finished = hasShipmentBatchFinished(batch);
  const failed = batch.counts.shipmentFailures + batch.counts.postProcessingFailed;
  const completedAt = finished
    ? new Date(batch.completedAt ?? job.completedAt ?? Date.now())
    : undefined;

  return {
    ...next,
    shipmentBatch: finished && completedAt
      ? {
          ...batch,
          status: batch.status === 'COMPLETED' || batch.status === 'COMPLETED_WITH_ERRORS'
            ? batch.status
            : failed > 0 ? 'COMPLETED_WITH_ERRORS' : 'COMPLETED',
          completedAt: completedAt.toISOString(),
        }
      : batch,
    completed: batch.counts.postProcessingCompleted,
    failed,
    status: finished ? 'completed' : 'running',
    completedAt,
  };
}
